// useOnline.js — Capa 2 del offline del chofer
// Hook que reporta si hay conexión REAL con el servidor.
//
// Estrategia de batería (decisión de diseño):
//   - Escucha los eventos 'online'/'offline' del navegador (gratis, los dispara el SO).
//   - NO hace polling activo por defecto. El ping al backend solo se dispara cuando
//     alguien lo pide explícitamente (verificarAhora), típicamente la capa de sync
//     cuando hay algo pendiente en la cola.
//   - Así, sin nada que sincronizar, el consumo de batería es prácticamente nulo.
//
// Devuelve: { online, verificando, verificarAhora }
//   online        -> boolean (mejor estimación actual de conectividad con el server)
//   verificando   -> boolean (hay un ping en curso)
//   verificarAhora-> función async que hace un ping real y actualiza 'online'

import { useState, useEffect, useCallback, useRef } from 'react';

// Ping ligero al backend. Usa un endpoint barato. Si responde (aunque sea 401),
// significa que HAY servidor → estamos online. Solo el error de red = offline.
async function pingServidor() {
  try {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 4000); // corta a los 4s
    const res = await fetch('/api/health', {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
    });
    clearTimeout(t);
    // Cualquier respuesta HTTP (200, 401, 404...) implica que el servidor contestó.
    return res != null;
  } catch {
    // Error de red / timeout / abort = sin conexión real
    return false;
  }
}

export default function useOnline() {
  // Estado inicial: lo que dice el navegador (optimista).
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [verificando, setVerificando] = useState(false);
  const montado = useRef(true);

  // Ping real bajo demanda. Devuelve el booleano y actualiza el estado.
  const verificarAhora = useCallback(async () => {
    if (!montado.current) return online;
    setVerificando(true);
    const ok = await pingServidor();
    if (montado.current) {
      setOnline(ok);
      setVerificando(false);
    }
    return ok;
  }, [online]);

  useEffect(() => {
    montado.current = true;

    // Evento del navegador: se conectó una red. Confirmamos con un ping real
    // (porque "red conectada" no garantiza que el servidor responda).
    const onOnline = () => { verificarAhora(); };
    // Evento del navegador: se perdió la red. Confiamos directo (sin ping).
    const onOffline = () => { if (montado.current) setOnline(false); };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      montado.current = false;
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [verificarAhora]);

  return { online, verificando, verificarAhora };
}
